import type { OpcaoSelect } from "../components/ui/CampoSelectCustom";

const PARAM_MODAL_NOVO = "modal";
const PARAM_RETORNO_TIPO = "retornoSelect";
const PARAM_RETORNO_TOKEN = "retornoToken";
const EVENTO_RETORNO = "cadastro-rapido-select:salvo";

export interface CadastroRapidoRetorno {
  tipo: string;
  token: string;
}

export interface MensagemCadastroRapidoSelect extends CadastroRapidoRetorno {
  evento: typeof EVENTO_RETORNO;
  opcao: OpcaoSelect;
  registro?: Record<string, unknown>;
}

type RegistroCadastroRapido = Record<string, unknown>;

function textoRegistro(registro: RegistroCadastroRapido, chaves: string[]): string {
  for (const chave of chaves) {
    const valor = String(registro[chave] ?? "").trim();
    if (valor) return valor;
  }
  return "";
}

function textoPrimeiro(registro: RegistroCadastroRapido, chaves: string[], fallback = ""): string {
  return textoRegistro(registro, chaves) || fallback;
}

function rotuloComCodigo(codigo: string, nome: string): string {
  if (!codigo) return nome;
  if (!nome) return codigo;
  return `${codigo} - ${nome}`;
}

export function montarOpcaoCadastroRapido(tipo: string, registro: RegistroCadastroRapido): OpcaoSelect | null {
  const tipoNormalizado = String(tipo || "").trim();

  if (tipoNormalizado === "planoConta") {
    const id = textoRegistro(registro, ["id"]);
    const nome = textoRegistro(registro, ["nome", "name"]);
    return id && nome ? { valor: id, rotulo: rotuloComCodigo(textoRegistro(registro, ["codigo"]), nome) } : null;
  }

  if (tipoNormalizado === "faixaPreco") {
    const id = textoPrimeiro(registro, ["id"]);
    const titulo = textoPrimeiro(registro, ["faixaPreco", "nome", "rotulo"], id);
    if (!id || !titulo) return null;
    const modalidade = textoRegistro(registro, ["tipo"]).toUpperCase();
    const de = modalidade === "TAMANHO"
      ? textoRegistro(registro, ["tamanhoDeCm"])
      : textoRegistro(registro, ["quantidadeDe"]);
    const ate = modalidade === "TAMANHO"
      ? textoRegistro(registro, ["tamanhoAteCm"])
      : textoRegistro(registro, ["quantidadeAte"]);
    const detalhe = modalidade === "TAMANHO"
      ? `Tamanho ${de || "0"} cm até ${ate || "0"} cm`
      : modalidade === "QUANTIDADE"
        ? `Quantidade ${de || "0"} até ${ate || "0"}`
        : `Preço ${titulo}`;
    return { valor: id, rotulo: titulo, detalhe, dadoBanco: true };
  }

  if (tipoNormalizado === "cor") {
    const nome = textoPrimeiro(registro, ["nome", "name", "rotulo"]);
    if (!nome) return null;
    return {
      valor: nome,
      rotulo: nome,
      cor: textoRegistro(registro, ["hex", "cor"]),
    };
  }

  if (tipoNormalizado === "tamanho") {
    const nome = textoPrimeiro(registro, ["nome", "name", "rotulo"]);
    if (!nome) return null;
    const grupo = textoRegistro(registro, ["grupo"]);
    return {
      valor: nome,
      rotulo: grupo ? `${nome} - ${grupo}` : nome,
      busca: grupo ? `${nome} ${grupo}` : nome,
    };
  }

  if (tipoNormalizado === "servico") {
    const nome = textoPrimeiro(registro, ["nome", "name", "rotulo"]);
    if (!nome) return null;
    return { valor: nome, rotulo: nome };
  }

  if (tipoNormalizado === "unidadeMedida") {
    const id = textoPrimeiro(registro, ["id", "codUnidade", "codUnidadeMedida"]);
    const nome = textoPrimeiro(registro, ["nome", "name", "rotulo"], id);
    if (!id && !nome) return null;
    const sigla = textoRegistro(registro, ["sigla"]);
    return { valor: id || nome, rotulo: sigla ? rotuloComCodigo(sigla, nome) : nome };
  }

  if (tipoNormalizado === "categoriaEstoque") {
    const id = textoPrimeiro(registro, ["id", "codCategoria", "categoriaId"]);
    const nome = textoPrimeiro(registro, ["nome", "name", "rotulo"], id);
    if (!id && !nome) return null;
    return { valor: id || nome, rotulo: nome };
  }

  if (tipoNormalizado === "detalheFicha") {
    const nome = textoPrimeiro(registro, ["nome", "name", "rotulo"]);
    if (!nome) return null;
    return {
      valor: nome,
      rotulo: nome,
      imagemUrl: textoRegistro(registro, ["imagemUrl", "imagemPath"]),
    };
  }

  if (tipoNormalizado === "empresa") {
    const id = textoPrimeiro(registro, ["codEmpresa", "id"]);
    const nome = textoPrimeiro(registro, ["fantasia", "name", "nome", "razaoSocial"], id);
    if (!id && !nome) return null;
    return { valor: id || nome, rotulo: nome };
  }

  if (tipoNormalizado === "cliente") {
    const id = textoPrimeiro(registro, ["codCliente", "id"]);
    const nome = textoPrimeiro(registro, ["name", "nome", "fantasia"], id);
    if (!id && !nome) return null;
    return {
      valor: id || nome,
      rotulo: nome,
      imagemUrl: textoRegistro(registro, ["logoUrl"]),
      avatarTexto: nome,
      busca: textoRegistro(registro, ["email", "cpf", "cnpj", "fantasia"]),
      dadoBanco: true,
    };
  }

  if (tipoNormalizado === "colaborador") {
    const id = textoPrimeiro(registro, ["codColaborador", "id"]);
    const nome = textoPrimeiro(registro, ["name", "nome"], id);
    if (!id && !nome) return null;
    return {
      valor: id || nome,
      rotulo: nome,
      avatarTexto: nome,
      busca: textoRegistro(registro, ["email", "cpf"]),
      dadoBanco: true,
    };
  }

  if (tipoNormalizado === "produto") {
    const id = textoPrimeiro(registro, ["codProduto", "id"]);
    const nome = textoPrimeiro(registro, ["nome", "name"], id);
    if (!id && !nome) return null;
    const sku = textoRegistro(registro, ["codigoSku", "sku"]);
    return {
      valor: id || nome,
      rotulo: rotuloComCodigo(sku, nome),
      imagemUrl: textoRegistro(registro, ["imagemUrl", "logoUrl"]),
      estoqueQuantidade: Number(registro.quantidadeEstoque || 0),
      busca: textoRegistro(registro, ["codigoBarras", "codigoExterno", "marca", "modelo"]),
    };
  }

  if (tipoNormalizado === "banco") {
    const id = textoPrimeiro(registro, ["id", "codBanco"]);
    const nome = textoPrimeiro(registro, ["nomeBanco", "nome", "name"], id);
    if (!id && !nome) return null;
    return {
      valor: id || nome,
      rotulo: nome,
      imagemUrl: textoRegistro(registro, ["logoUrl"]),
    };
  }

  if (tipoNormalizado === "fornecedor") {
    const id = textoPrimeiro(registro, ["codFornecedor", "id"]);
    const nome = textoPrimeiro(registro, ["nomeRazaoSocial", "razaoSocial", "fantasia", "nome", "name"], id);
    if (!id && !nome) return null;
    return {
      valor: id || nome,
      rotulo: nome,
      imagemUrl: textoRegistro(registro, ["logoUrl"]),
    };
  }

  if (tipoNormalizado === "transportadora") {
    const id = textoPrimeiro(registro, ["codTransportadora", "id"]);
    const nome = textoPrimeiro(registro, ["nome", "fantasia", "name"], id);
    if (!id && !nome) return null;
    return {
      valor: id || nome,
      rotulo: nome,
      imagemUrl: textoRegistro(registro, ["logoUrl"]),
    };
  }

  if (tipoNormalizado === "etapa") {
    const id = textoPrimeiro(registro, ["codEtapa", "id"]);
    const nome = textoPrimeiro(registro, ["nome", "name"], id);
    if (!id && !nome) return null;
    return { valor: id || nome, rotulo: nome, busca: textoRegistro(registro, ["responsavel", "descricao"]) };
  }

  if (tipoNormalizado === "perfil") {
    const id = textoPrimeiro(registro, ["codPerfil", "id"]);
    const nome = textoPrimeiro(registro, ["nomePerfil", "nome", "name"], id);
    if (!id && !nome) return null;
    return { valor: id || nome, rotulo: nome, busca: textoRegistro(registro, ["tipoPerfil", "descricao"]) };
  }

  const id = textoPrimeiro(registro, ["id", "codigo", "valor"]);
  const nome = textoPrimeiro(registro, ["nome", "name", "rotulo"], id);
  if (!id && !nome) return null;
  return { valor: id || nome, rotulo: nome };
}

export function criarTokenCadastroRapido(tipo: string): string {
  const sufixo = typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  return `${tipo}-${sufixo}`;
}

export function montarUrlCadastroRapido(rota: string, tipo: string, token: string): string {
  const url = new URL(rota, window.location.origin);
  url.searchParams.set(PARAM_MODAL_NOVO, "novo");
  url.searchParams.set(PARAM_RETORNO_TIPO, tipo);
  url.searchParams.set(PARAM_RETORNO_TOKEN, token);
  return `${url.pathname}${url.search}${url.hash}`;
}

export function abrirCadastroRapidoSelect(rota: string, tipo: string): CadastroRapidoRetorno {
  const token = criarTokenCadastroRapido(tipo);
  window.open(montarUrlCadastroRapido(rota, tipo, token), "_blank");
  return { tipo, token };
}

export function lerCadastroRapidoDaUrl(search = window.location.search): CadastroRapidoRetorno | null {
  const params = new URLSearchParams(search);
  const modalNovo = params.get(PARAM_MODAL_NOVO) === "novo";
  const tipo = params.get(PARAM_RETORNO_TIPO) || "";
  const token = params.get(PARAM_RETORNO_TOKEN) || "";
  if (!modalNovo || !tipo || !token) return null;
  return { tipo, token };
}

export function ehMensagemCadastroRapidoSelect(data: unknown): data is MensagemCadastroRapidoSelect {
  if (!data || typeof data !== "object") return false;
  const registro = data as Record<string, unknown>;
  return registro.evento === EVENTO_RETORNO &&
    typeof registro.tipo === "string" &&
    typeof registro.token === "string" &&
    typeof registro.opcao === "object" &&
    registro.opcao !== null;
}

export function emitirCadastroRapidoSalvo({
  tipo,
  token,
  opcao,
  registro,
}: CadastroRapidoRetorno & {
  opcao: OpcaoSelect;
  registro?: RegistroCadastroRapido;
}): boolean {
  if (!window.opener || window.opener.closed) return false;

  window.opener.postMessage(
    {
      evento: EVENTO_RETORNO,
      tipo,
      token,
      opcao,
      registro,
    } satisfies MensagemCadastroRapidoSelect,
    window.location.origin
  );
  window.close();
  return true;
}
